import { useEffect, useState, useCallback } from 'react';
import { logger } from '../utils/logger';
import { useNavigate } from 'react-router-dom';
import { Settings, Image, Music, ShoppingBag, Users, Bell, FolderOpen, BarChart3, Mail, DollarSign, Sparkles, Vote, Video, Radio } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AdminSiteSettings,
  AdminTracks,
  AdminAlbums,
  AdminMerch,
  AdminPresaves,
  AdminFiles,
  AdminSubscribers,
  AdminWaitlist,
  AdminVoting,
  AdminAnalytics,
  AdminEmailCampaigns,
  AdminRoyalties,
  AdminVideos,
  AdminLiveEvents
} from '../components/admin';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  const [settings, setSettings] = useState({
    artist_name: '',
    artist_tagline: '',
    hero_title: '',
    hero_description: '',
    hero_image: '',
    announcement_text: '',
    announcement_enabled: false,
    featured_album_id: '',
    social_instagram: '',
    social_twitter: '',
    social_youtube: ''
  });
  
  const [tracks, setTracks] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [merch, setMerch] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [presaves, setPresaves] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [settingsRes, tracksRes, albumsRes, merchRes, subsRes, presavesRes, filesRes] = await Promise.all([
        axios.get(`${API_URL}/settings`),
        axios.get(`${API_URL}/tracks`),
        axios.get(`${API_URL}/albums`),
        axios.get(`${API_URL}/merch`),
        axios.get(`${API_URL}/admin/subscribers`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/presaves`).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/admin/files`).catch(() => ({ data: [] }))
      ]);
      setSettings(settingsRes.data);
      setTracks(tracksRes.data);
      setAlbums(albumsRes.data);
      setMerch(merchRes.data);
      setSubscribers(subsRes.data);
      setPresaves(presavesRes.data);
      setUploadedFiles(filesRes.data);
    } catch (error) {
      logger.error('Failed to fetch data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    // Check if user has admin role
    if (user && user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    fetchData();
  }, [isAuthenticated, user, navigate, fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto mb-4" />
          <p className="text-gray-400">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" data-testid="admin-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <Settings className="w-8 h-8 text-purple-400" />
          Admin Panel
        </h1>

        <Tabs defaultValue="analytics" className="w-full">
          <div className="mb-8 bg-white/5 p-1 rounded-lg overflow-x-auto scrollbar-hide">
            <TabsList className="flex gap-1 w-max min-w-full">
              <TabsTrigger value="analytics" className="data-[state=active]:bg-green-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <BarChart3 className="w-4 h-4 mr-1" /> Analytics
              </TabsTrigger>
              <TabsTrigger value="site" className="data-[state=active]:bg-purple-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <Settings className="w-4 h-4 mr-1" /> Site
              </TabsTrigger>
              <TabsTrigger value="tracks" className="data-[state=active]:bg-purple-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <Music className="w-4 h-4 mr-1" /> Tracks
              </TabsTrigger>
              <TabsTrigger value="albums" className="data-[state=active]:bg-purple-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <Image className="w-4 h-4 mr-1" /> Albums
              </TabsTrigger>
              <TabsTrigger value="merch" className="data-[state=active]:bg-purple-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <ShoppingBag className="w-4 h-4 mr-1" /> Merch
              </TabsTrigger>
              <TabsTrigger value="presaves" className="data-[state=active]:bg-pink-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <Bell className="w-4 h-4 mr-1" /> Presaves
              </TabsTrigger>
              <TabsTrigger value="files" className="data-[state=active]:bg-cyan-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <FolderOpen className="w-4 h-4 mr-1" /> Files
              </TabsTrigger>
              <TabsTrigger value="subscribers" className="data-[state=active]:bg-purple-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <Users className="w-4 h-4 mr-1" /> Fans
              </TabsTrigger>
              <TabsTrigger value="waitlist" className="data-[state=active]:bg-cyan-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm" data-testid="admin-tab-waitlist">
                <Sparkles className="w-4 h-4 mr-1" /> Waitlist
              </TabsTrigger>
              <TabsTrigger value="voting" className="data-[state=active]:bg-cyan-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm" data-testid="admin-tab-voting">
                <Vote className="w-4 h-4 mr-1" /> Voting
              </TabsTrigger>
              <TabsTrigger value="campaigns" className="data-[state=active]:bg-cyan-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <Mail className="w-4 h-4 mr-1" /> Campaigns
              </TabsTrigger>
              <TabsTrigger value="royalties" className="data-[state=active]:bg-green-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <DollarSign className="w-4 h-4 mr-1" /> Royalties
              </TabsTrigger>
              <TabsTrigger value="videos" className="data-[state=active]:bg-purple-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm">
                <Video className="w-4 h-4 mr-1" /> Videos
              </TabsTrigger>
              <TabsTrigger value="live" className="data-[state=active]:bg-red-500/20 px-3 py-2 rounded-md whitespace-nowrap text-sm" data-testid="admin-tab-live">
                <Radio className="w-4 h-4 mr-1" /> Live
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="analytics">
            <AdminAnalytics />
          </TabsContent>

          <TabsContent value="site">
            <AdminSiteSettings settings={settings} setSettings={setSettings} albums={albums} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="tracks">
            <AdminTracks tracks={tracks} albums={albums} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="albums">
            <AdminAlbums albums={albums} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="merch">
            <AdminMerch merch={merch} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="presaves">
            <AdminPresaves presaves={presaves} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="files">
            <AdminFiles files={uploadedFiles} onRefresh={fetchData} />
          </TabsContent>

          <TabsContent value="subscribers">
            <AdminSubscribers subscribers={subscribers} />
          </TabsContent>

          <TabsContent value="waitlist">
            <AdminWaitlist />
          </TabsContent>

          <TabsContent value="voting">
            <AdminVoting />
          </TabsContent>

          <TabsContent value="campaigns">
            <AdminEmailCampaigns />
          </TabsContent>

          <TabsContent value="royalties">
            <AdminRoyalties />
          </TabsContent>

          <TabsContent value="videos">
            <AdminVideos />
          </TabsContent>

          <TabsContent value="live">
            <AdminLiveEvents />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
